import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { setAuthToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_FULL, LOGO_ROUND } from "@/lib/constants";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { tx, lz } from "@/i18n";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null); // null=checking, false=invalid, {name,email}
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setInfo(false); return; }
    axios.get(`${API}/public/invite/${token}`).then(({ data }) => setInfo(data)).catch(() => setInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(tx("Wachtwoord moet minstens 6 tekens zijn")); return; }
    if (password !== confirm) { toast.error(tx("Wachtwoorden komen niet overeen")); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/auth/activate`, { token, password }, { withCredentials: true });
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data);
      toast.success(tx("Account geactiveerd — welkom!"));
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || tx("Activeren mislukt"));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1499898595565-f424ed1d075c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt={tx("Costa")} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-secondary/40" />
        <div className="absolute bottom-10 left-10 text-white max-w-sm">
          <img src={LOGO_ROUND} alt={tx("Costa Key")} className="h-32 w-32 rounded-full shadow-2xl shadow-black/30 mb-5" />
          <h2 className="font-display font-bold text-3xl">{tx("Costa Key Portaal")}</h2>
          <p className="mt-2 text-white/90">{tx("Activeer je account en kies een wachtwoord.")}</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="inline-flex items-center mb-8">
            <img src={LOGO_FULL} alt={tx("Costa Key")} className="h-14 w-auto object-contain" />
          </Link>
          {info === null ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
          ) : info === false ? (
            <div data-testid="activate-invalid">
              <h1 className="font-display font-bold text-2xl tracking-tight">{tx("Uitnodiging ongeldig")}</h1>
              <p className="text-muted-foreground mt-2">{tx("Deze uitnodigingslink is ongeldig of verlopen. Vraag de beheerder om een nieuwe uitnodiging.")}</p>
              <Link to="/login" className="text-primary font-semibold text-sm mt-4 inline-block">{tx("Naar inloggen")}</Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="activate-form">
              <h1 className="font-display font-bold text-3xl tracking-tight">{tx("Account activeren")}</h1>
              <p className="text-muted-foreground mt-1 mb-6">{tx("Welkom")} {info.name}{tx("! Stel een wachtwoord in voor")} <span className="font-medium text-foreground">{info.email}</span>.</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="pwd">{tx("Wachtwoord")}</Label>
                  <Input id="pwd" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="activate-password-input" />
                </div>
                <div>
                  <Label htmlFor="pwd2">{tx("Herhaal wachtwoord")}</Label>
                  <Input id="pwd2" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="activate-confirm-input" />
                </div>
              </div>
              <Button type="submit" disabled={saving} className="w-full mt-6 bg-primary hover:bg-primary/90 gap-2" data-testid="activate-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {tx("Account activeren")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
